import {
  FormSectionState,
  FormState,
  INIT_MODES,
  PowerState,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { signalStoreFeature, type, withState } from '@ngrx/signals';
import { on, withReducer } from '@ngrx/signals/events';
import { initStateField } from '../../utils/store.utility';
import { EdnFileType } from '../report-dependencies';
import { powerCalculationEvents, powerEvents, powerFlowMeterEvents, shorePowerDocumentEvents } from './power-events';

const clearUploading = (ednType: EdnFileType, uploadingEdnFiles: Partial<Record<EdnFileType, boolean>>) => {
  const { [ednType]: _removed, ...rest } = uploadingEdnFiles;
  return rest;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function withPowerReducer<_>() {
  return signalStoreFeature(
    type<{ state: { formState: FormState } }>(),
    withReducer(
      on(powerFlowMeterEvents.initialiseFlowMeterInstallation, ({ payload: { paths } }) => (state) => {
        const formState = state['formState'];
        const existing = (formState['flowMeterInstallation'] as FormSectionState) ?? {};
        const pathSet = new Set(paths);

        const hasMissing = paths.some((path) => !(path in existing));
        const hasExtra = Object.keys(existing).some((key) => !pathSet.has(key));
        if (!hasMissing && !hasExtra) return {};

        const reconciled: FormSectionState = Object.fromEntries(
          paths.map((path) => [path, existing[path] ?? initStateField<number>()])
        );

        return {
          formState: {
            ...formState,
            flowMeterInstallation: reconciled,
          },
        };
      })
    )
  );
}

/**
 * The Average TC RPM Power that the server last gave. It starts with the saved
 * value of an imported report, so a save before the server answers keeps it.
 * A failed call keeps the last value.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function withTcRpmPowerReducer<_>() {
  return signalStoreFeature(
    withState({ tcRpmPowerResult: undefined as number | undefined }),
    withReducer(
      on(powerEvents.initialiseFormState, ({ payload: { initialData, initMode } }) => ({
        tcRpmPowerResult:
          initMode === INIT_MODES.IMPORT ? (initialData as PowerState).mainEngine?.tcRpmPower : undefined,
      })),
      on(powerCalculationEvents.getTcRpmPowerSuccess, ({ payload: { tcrpmPower } }) => ({
        tcRpmPowerResult: tcrpmPower,
      })),
      on(powerCalculationEvents.clearTcRpmPower, () => ({ tcRpmPowerResult: undefined }))
    )
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function withShorePowerDocumentReducer<_>() {
  return signalStoreFeature(
    type<{
      state: {
        pendingEdnFiles: Partial<Record<EdnFileType, File>>;
        uploadingEdnFiles: Partial<Record<EdnFileType, boolean>>;
      };
    }>(),
    withReducer(
      on(shorePowerDocumentEvents.queueEdnFile, ({ payload: { ednType, file } }) => (state) => ({
        pendingEdnFiles: { ...state.pendingEdnFiles, [ednType]: file },
      })),
      on(shorePowerDocumentEvents.dequeueEdnFile, ({ payload: { ednType } }) => (state) => {
        const { [ednType]: _removed, ...rest } = state.pendingEdnFiles;
        return { pendingEdnFiles: rest };
      }),
      // Drives the document-upload button's spinner / suppresses its "not uploaded yet" warning
      // while the deferred (queue-until-save) upload is actually in flight.
      on(shorePowerDocumentEvents.uploadEdnFile, ({ payload: { ednType } }) => (state) => ({
        uploadingEdnFiles: { ...state.uploadingEdnFiles, [ednType]: true },
      })),
      on(shorePowerDocumentEvents.uploadEdnFileSuccess, ({ payload: { ednType } }) => (state) => ({
        uploadingEdnFiles: clearUploading(ednType, state.uploadingEdnFiles),
      })),
      on(shorePowerDocumentEvents.uploadEdnFileFailed, ({ payload: { ednType } }) => (state) => ({
        uploadingEdnFiles: clearUploading(ednType, state.uploadingEdnFiles),
      }))
    )
  );
}
